import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import useStore from '@/store/useStore';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { LiveScoreboard } from '@/components/match/LiveScoreboard';
import { Undo } from 'lucide-react';
import ScoringGuide from '@/components/help/ScoringGuide';

const MatchScoringPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') || undefined;
  const { matches, teams, updateScore, updateMatchStatus, fetchMatches, fetchTeams } = useStore();
  const [recentScores, setRecentScores] = useState<unknown[]>([]);
  
  // Refresh data when component mounts
  useEffect(() => {
    const refreshData = async () => {
      try {
        await fetchMatches();
        await fetchTeams();
      } catch (error) {
        console.error('Error refreshing data:', error);
      }
    };
    
    refreshData();
  }, [fetchMatches, fetchTeams]);
  
  const match = matches.find(m => m.id === id);
  
  console.log('MatchScoringPage - match.matchDate:', match?.matchDate);
  console.log('MatchScoringPage - match.wicketsPerInnings:', match?.wicketsPerInnings);
  
  if (!match) {
    return (
      <div className="text-center py-8 sm:py-12 px-4">
        <h2 className="text-xl sm:text-2xl font-bold mb-4">Match not found</h2>
        <Button onClick={() => navigate('/matches')}>Return to Matches</Button>
      </div>
    );
  }
  
  const team1 = teams.find(t => t.id === match.team1Id);
  const team2 = teams.find(t => t.id === match.team2Id);
  
  // Filter the teams to only include the two teams in the current match
  const currentMatchTeams = teams.filter(t => t.id === match.team1Id || t.id === match.team2Id);
  
  return (
    <div className="max-w-6xl mx-auto animate-fade-in pb-16 sm:pb-0">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
        <Button 
          variant="ghost" 
          onClick={() => navigate("/matches")}
          className="self-start"
        >
          ← Back to Matches
        </Button>
        
        {/* Scoring Guide */}
        <ScoringGuide />
      </div>
      
      <div className="bg-card text-foreground p-4 sm:p-6 rounded-lg mb-4 sm:mb-6 border border-border">
        <h1 className="text-xl sm:text-2xl font-bold mb-2">{team1?.name} vs {team2?.name}</h1>
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 sm:gap-0">
          <div>
            <p className="text-sm sm:text-base">{match.city}</p>
            <p className="text-xs sm:text-sm opacity-80">{new Date(match.matchDate).toLocaleDateString()}</p>
          </div>
          <div className="bg-background text-foreground px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-semibold border border-input">
            {match.status.toUpperCase()}
          </div>
        </div>
      </div>
      
      <div className="grid grid-cols-1 gap-4 sm:gap-6">
        <div className="w-full">
          <LiveScoreboard 
            match={match}
            teams={currentMatchTeams}
            onScoreUpdate={updateScore}
            onMatchStatusChange={updateMatchStatus}
            initialTab={initialTab}
          />
        </div>
        
        {/* <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Scorecard</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="border rounded-lg p-4">
                  <h3 className="font-semibold mb-2 flex items-center gap-2">
                    {team1?.name}
                    {team1?.color && (
                      <div 
                        className="w-4 h-4 rounded border"
                        style={{ backgroundColor: team1.color }}
                      />
                    )}
                    {match.currentBattingTeam === team1?.id && (
                      <Badge className="bg-green-500">Batting</Badge>
                    )}
                  </h3>
                  <div className="flex justify-between">
                    <span>
                      {match.team1Score.runs}/{match.team1Score.wickets}
                    </span>
                    <span>
                      {match.team1Score.overs}.{match.team1Score.balls} overs
                    </span>
                  </div>
                </div>
                
                <div className="border rounded-lg p-4">
                  <h3 className="font-semibold mb-2 flex items-center gap-2">
                    {team2?.name}
                    {team2?.color && (
                      <div 
                        className="w-4 h-4 rounded border"
                        style={{ backgroundColor: team2.color }}
                      />
                    )}
                    {match.currentBattingTeam === team2?.id && (
                      <Badge className="bg-green-500">Batting</Badge>
                    )}
                  </h3>
                  <div className="flex justify-between">
                    <span>
                      {match.team2Score.runs}/{match.team2Score.wickets}
                    </span>
                    <span>
                      {match.team2Score.overs}.{match.team2Score.balls} overs
                    </span>
                  </div>
                </div>
              </div>
              
              <div className="mt-6 pt-6 border-t">
                <h3 className="font-semibold mb-4">Match Info</h3>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-600">City</span>
                    <span>{match.city}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Date</span>
                    <span>{new Date(match.date).toLocaleDateString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Status</span>
                    <span className="capitalize">{match.status}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Match Type</span>
                    <span>{match.matchType}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Overs</span>
                    <span>{match.totalOvers}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Current Innings</span>
                    <span>{match.currentInnings}</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div> */}
      </div>
    </div>
  );
};

export default MatchScoringPage;
